import React from "react";
import { Table, Form, Spinner } from "react-bootstrap";
import CustomizedDropdown from "components/wrapped-component/customized-dropdown/CustomizedDropdown";
import Textbox from "components/wrapped-component/hint-controls/Textbox";
import "./die-type-mapping-popup.scss";

type DieTypeName = string;

interface CharacterMapping {
  dieType: DieTypeName;
  hardBinNo: string;
  isHardBinNoChecked: boolean;
  hardBinName: string;
  softBinNo: string;
  isSoftBinNoChecked: boolean;
  softBinName: string;
  pfFlag: string;
  isPFFlagChecked: boolean;
}

interface DieTypeMappingDataTableProps {
  uniqueCharacters: string[];
  mappings: Record<string, CharacterMapping>;
  dieTypeOptions: Array<[string, string]>;
  isLoadingDieTypes: boolean;
  binMap: "None" | "User Defined";
  headerCheckboxes: {
    hardBinNo: boolean;
    hardBinName: boolean;
    softBinNo: boolean;
    softBinName: boolean;
    pfFlag: boolean;
  };
  onDieTypeChange: (character: string, dieType: string) => void;
  onInputChange: (character: string, field: keyof CharacterMapping, value: string) => void;
  onDropdownChange: (character: string, field: keyof CharacterMapping, value: string) => void;
  onHeaderCheckboxChange: (field: keyof DieTypeMappingDataTableProps["headerCheckboxes"], checked: boolean) => void;
}

const PF_FLAG_OPTIONS: Array<[string, string]> = [
  ["Pass", "Pass"],
  ["Fail", "Fail"],
];

class DieTypeMappingDataTable extends React.Component<DieTypeMappingDataTableProps> {
  render() {
    const {
      uniqueCharacters,
      mappings,
      dieTypeOptions,
      isLoadingDieTypes,
      binMap,
      headerCheckboxes,
      onDieTypeChange,
      onInputChange,
      onDropdownChange,
      onHeaderCheckboxChange,
    } = this.props;

    return (
      <div className="die-type-mapping-table-container">
        <Table striped bordered hover responsive className="die-type-mapping-table">
          <thead>
            <tr>
              <th className="die-character-column">Die Character</th>
              <th className="die-type-column">Die Type</th>
              <th className="bin-column">
                <Form.Check
                  type="checkbox"
                  id="hardbin-no-checkbox-all"
                  checked={headerCheckboxes.hardBinNo}
                  onChange={(e) => onHeaderCheckboxChange("hardBinNo", e.target.checked)}
                  disabled={binMap === "None"}
                  className="header-checkbox"
                />
                HardBin #
              </th>
              <th className="bin-column">
                <Form.Check
                  type="checkbox"
                  id="hardbin-name-checkbox-all"
                  checked={headerCheckboxes.hardBinName}
                  onChange={(e) => onHeaderCheckboxChange("hardBinName", e.target.checked)}
                  disabled={binMap === "None"}
                  className="header-checkbox"
                />
                HardBin Name
              </th>
              <th className="bin-column">
                <Form.Check
                  type="checkbox"
                  id="softbin-no-checkbox-all"
                  checked={headerCheckboxes.softBinNo}
                  onChange={(e) => onHeaderCheckboxChange("softBinNo", e.target.checked)}
                  disabled={binMap === "None"}
                  className="header-checkbox"
                />
                SoftBin #
              </th>
              <th className="bin-column">
                <Form.Check
                  type="checkbox"
                  id="softbin-name-checkbox-all"
                  checked={headerCheckboxes.softBinName}
                  onChange={(e) => onHeaderCheckboxChange("softBinName", e.target.checked)}
                  disabled={binMap === "None"}
                  className="header-checkbox"
                />
                SoftBin Name
              </th>
              <th className="bin-column">
                <Form.Check
                  type="checkbox"
                  id="pfflag-checkbox-all"
                  checked={headerCheckboxes.pfFlag}
                  onChange={(e) => onHeaderCheckboxChange("pfFlag", e.target.checked)}
                  disabled={binMap === "None"}
                  className="header-checkbox"
                />
                P/F Flag
              </th>
            </tr>
          </thead>
          <tbody>
            {uniqueCharacters?.map((char) => {
              const mapping = mappings[char] || {
                dieType: "Probed Die",
                hardBinNo: "",
                isHardBinNoChecked: false,
                hardBinName: "",
                softBinNo: "",
                isSoftBinNoChecked: false,
                softBinName: "",
                pfFlag: "",
                isPFFlagChecked: false,
              };
              return (
                <tr key={char}>
                  <td className="die-character-cell">{char}</td>
                  <td className="table-cell-middle">
                    {isLoadingDieTypes ? (
                      <Spinner animation="border" size="sm" />
                    ) : dieTypeOptions.length > 0 ? (
                      <CustomizedDropdown
                        variant="clear"
                        full
                        list={dieTypeOptions}
                        selectedValue={mapping.dieType}
                        onChange={(value: string) => onDieTypeChange(char, value)}
                        key={`die-type-${char}-${dieTypeOptions.length}`}
                      />
                    ) : (
                      <span>No die types available</span>
                    )}
                  </td>
                  <td className="table-cell-middle">
                    <Textbox
                      name={`hardBinNo-${char}`}
                      type="number"
                      value={mapping.hardBinNo}
                      onChange={(e: any) => onInputChange(char, "hardBinNo", e.target.value)}
                      disabled={!headerCheckboxes.hardBinNo || binMap === "None"}
                      className="full-width-input"
                    />
                  </td>
                  <td className="table-cell-middle">
                    <Textbox
                      name={`hardBinName-${char}`}
                      type="text"
                      value={mapping.hardBinName}
                      onChange={(e: any) => onInputChange(char, "hardBinName", e.target.value)}
                      disabled={!headerCheckboxes.hardBinName || binMap === "None"}
                      className="full-width-input"
                    />
                  </td>
                  <td className="table-cell-middle">
                    <Textbox
                      name={`softBinNo-${char}`}
                      type="number"
                      value={mapping.softBinNo}
                      onChange={(e: any) => onInputChange(char, "softBinNo", e.target.value)}
                      disabled={!headerCheckboxes.softBinNo || binMap === "None"}
                      className="full-width-input"
                    />
                  </td>
                  <td className="table-cell-middle">
                    <Textbox
                      name={`softBinName-${char}`}
                      type="text"
                      value={mapping.softBinName}
                      onChange={(e: any) => onInputChange(char, "softBinName", e.target.value)}
                      disabled={!headerCheckboxes.softBinName || binMap === "None"}
                      className="full-width-input"
                    />
                  </td>
                  <td className="table-cell-middle">
                    <CustomizedDropdown
                      variant="clear"
                      full
                      list={PF_FLAG_OPTIONS}
                      selectedValue={mapping.pfFlag}
                      onChange={(value: string) => onDropdownChange(char, "pfFlag", value)}
                      disabled={!headerCheckboxes.pfFlag || binMap === "None"}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  }
}

export default DieTypeMappingDataTable;

